import React, { useState, useRef, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { AI } from '@/constants/testIds';

const SUGGESTIONS = [
  'Who should I contact today?',
  'Summarise my warm leads',
  'What opportunities am I missing?',
  'Draft a follow-up for my top prospect',
];

export default function AIChatDrawer({ open, setOpen }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, sending]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || sending) return;
    setMsgs((m) => [...m, { role: 'user', content: msg }]);
    setInput(''); setSending(true);
    try {
      const { data } = await api.post('/ai/chat', { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: 'assistant', content: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: 'assistant', content: 'Something went wrong. Try again.' }]);
    } finally { setSending(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid={AI.drawer} side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-5 py-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2 display text-lg">
            <Sparkles className="w-4 h-4 text-foreground" />
            AI Executive Assistant
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {msgs.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Ask me anything about your people, pipeline, or follow-ups.</p>
              <div className="space-y-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="w-full text-left text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} data-testid={AI.message(i)} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-sm text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === 'user' ? 'bg-primary text-foreground border border-yellow-300' : 'bg-zinc-100 border border-zinc-200'
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="px-3 py-2 rounded-sm bg-zinc-100 border border-zinc-200 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border p-3 flex items-center gap-2">
          <input
            data-testid={AI.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder="Ask anything…"
            className="flex-1 px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-foreground transition-colors"
          />
          <button data-testid={AI.send} onClick={() => send()} disabled={sending} className="px-3 py-2 bg-primary text-foreground rounded-sm hover:brightness-95 transition-colors disabled:opacity-50">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
